import React, { useCallback, useEffect, useState } from "react";
import {
  CarOutlined,
  CoffeeOutlined,
  EditOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlusOutlined,
  PoweroffOutlined
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Input,
  Progress,
  Row,
  Segmented,
  Select,
  Spin,
  Statistic,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  message,
  theme
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { isAuthenticated } from "../../Auth/auth";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import {
  clock,
  fetchExport,
  fetchProjects,
  fetchRange,
  fetchStatistics,
  fetchToday
} from "./attendanceApi";
import { saveFile } from "../../../native/files";
import DayEditor from "./DayEditor";
import TeamExportModal from "./TeamExportModal";
import { STATUS_META, TYPE_META, warningText } from "./labels";
import { formatMinutes } from "./stamps";
import { summarizeDay } from "./timeline";
import OfflineNotice from "../../../offline/OfflineNotice";
import { useOnSynced } from "../../../offline/hooks";
import "./AttendanceCSS.css";

const { Text } = Typography;
const DATE = "YYYY-MM-DD";
const hoursToMinutes = h => Math.round((h || 0) * 60);

// Buttons offered in each state; the time field above them decides when it happened
const ACTIONS = {
  NOT_STARTED: ["WORK", "TRAVEL"],
  CHECKED_OUT: ["WORK", "TRAVEL"],
  CHECKED_IN: ["BREAK", "TRAVEL", "STOP"],
  ON_BREAK: ["WORK", "STOP"],
  TRAVELLING: ["WORK", "BREAK", "STOP"]
};

/**
 * Arbeitszeitnachweis of one person: live clock (now or an entered time), today's blocks, and
 * the month with manual entry/correction of any day. `employeeId` shows someone else's times
 * (employee profile); without it the logged-in user's own.
 */
const AttendanceComp = ({ employeeId }) => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const ownId = isAuthenticated()?._id;
  const userId = employeeId || ownId;

  const [today, setToday] = useState(null);
  const [openDays, setOpenDays] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [month, setMonth] = useState(dayjs().startOf("month"));
  const [monthDays, setMonthDays] = useState([]);
  // "YYYY-MM-DD" → public holiday name of the shown month (company's Land)
  const [monthHolidays, setMonthHolidays] = useState({});
  const [teamExport, setTeamExport] = useState({ open: false, format: "pdf" });
  const [monthLoading, setMonthLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(null);
  const [timeMode, setTimeMode] = useState("now");
  const [customTime, setCustomTime] = useState(dayjs().format("HH:mm"));
  const [clockProject, setClockProject] = useState(null);
  const [editor, setEditor] = useState({ open: false, date: dayjs().format(DATE) });
  const [, setTick] = useState(0);
  // Days older than yesterday are locked without the "Attendance-corrections" permission
  const [canCorrectPast, setCanCorrectPast] = useState(false);
  const [canExportTeam, setCanExportTeam] = useState(false);

  const loadToday = useCallback(
    () =>
      fetchToday(userId)
        .then(res => {
          setToday(res.data);
          setOpenDays(res.openDays || []);
          setCanCorrectPast(Boolean(res.canCorrectPast));
          setCanExportTeam(Boolean(res.canExportTeam));
        })
        .catch(() => message.error(t("Failed to fetch today's attendance"))),
    [userId]
  );
  const loadStatistics = useCallback(
    () =>
      fetchStatistics(userId)
        .then(res => setStatistics(res.data))
        .catch(() => message.error(t("Failed to fetch statistics"))),
    [userId]
  );
  const loadMonth = useCallback(() => {
    setMonthLoading(true);
    return fetchRange(userId, month.startOf("month").toDate(), month.endOf("month").toDate())
      .then(res => {
        setMonthDays(Array.isArray(res.data) ? res.data : []);
        setMonthHolidays(res.holidays || {});
      })
      .catch(() => message.error(t("Failed to fetch attendance history")))
      .finally(() => setMonthLoading(false));
  }, [userId, month]);

  const refresh = () => Promise.all([loadToday(), loadStatistics(), loadMonth()]);
  // Clock actions queued offline reached the server: show the real day
  useOnSynced("attendance", item => item.target === userId && refresh());

  useEffect(() => {
    if (!userId) return;
    loadToday();
    loadStatistics();
    fetchProjects()
      .then(setProjects)
      .catch(() => setProjects([]));
  }, [userId]);

  useEffect(() => {
    if (userId) loadMonth();
  }, [loadMonth]);

  // Running clock: re-render every 30 s (every second is pointless for h:mm)
  useEffect(() => {
    const timer = setInterval(() => setTick(n => n + 1), 30 * 1000);
    return () => clearInterval(timer);
  }, []);

  const status = today?.status || "NOT_STARTED";
  const segments = today?.segments || [];
  const running = segments.find(s => !s.end);
  // Cheap enough to redo on every (30 s) render, so a running block keeps counting
  const live = summarizeDay(segments, {
    now: Date.now(),
    flatBreakMinutes: today?.flatBreakMinutes,
    targetMinutes: today?.summary?.targetMinutes
  });

  // The entered time is today's; one later than now means yesterday evening (after midnight)
  const clockTime = () => {
    if (timeMode === "now") return undefined;
    const [h, m] = customTime.split(":").map(Number);
    let at = dayjs().hour(h).minute(m).second(0).millisecond(0);
    if (at.isAfter(dayjs().add(2, "minute"))) at = at.subtract(1, "day");
    return at.toDate();
  };

  const act = async action => {
    if (timeMode === "custom" && !customTime) return message.warning(t("Enter a time"));
    setBusy(action);
    try {
      await clock(userId, {
        action,
        at: clockTime(),
        project: action === "WORK" || action === "TRAVEL" ? clockProject || undefined : undefined
      });
      message.success(
        {
          WORK: t("Work started"),
          TRAVEL: t("Travel started"),
          BREAK: t("Break started!"),
          STOP: t("End of day recorded")
        }[action]
      );
      setTimeMode("now");
      await refresh();
    } catch (error) {
      const data = error.response?.data;
      if (data?.code === "OPEN_DAY") {
        message.warning(t("A previous day is still open. Please enter its end time."));
        setEditor({ open: true, date: data.date });
      } else message.error(data?.message ? t(data.message) : t("Action failed"));
    } finally {
      setBusy(null);
    }
  };

  const actionButton = action => {
    const config = {
      WORK: {
        icon: <PlayCircleOutlined />,
        label:
          status === "ON_BREAK"
            ? t("Resume work")
            : status === "TRAVELLING"
              ? t("Arrived – start work")
              : t("Start work"),
        props: { type: "primary" }
      },
      TRAVEL: {
        icon: <CarOutlined />,
        label: status === "CHECKED_IN" ? t("Travel") : t("Start travel"),
        props: {}
      },
      BREAK: { icon: <CoffeeOutlined />, label: t("Break"), props: {} },
      STOP: {
        icon: <PoweroffOutlined />,
        label: status === "TRAVELLING" ? t("Home – end of day") : t("End of day"),
        props: { danger: true, type: "primary", className: "attendance-page__stop" }
      }
    }[action];
    return (
      <Button
        key={action}
        block
        size="large"
        icon={config.icon}
        loading={busy === action}
        disabled={Boolean(busy) && busy !== action}
        onClick={() => act(action)}
        {...config.props}
      >
        {config.label}
      </Button>
    );
  };

  const isLocked = day => !canCorrectPast && day < dayjs().subtract(1, "day").format(DATE);
  // Arbeitszeitnachweis of the selected month; the server renders all three formats
  const [exporting, setExporting] = useState(false);
  // key "pdf" | "xlsx" | "csv"; "team:<format>" opens the team dialog (choose employees)
  const exportMonth = async key => {
    const [scope, format] = key.includes(":") ? key.split(":") : ["self", key];
    if (scope === "team") return setTeamExport({ open: true, format });
    setExporting(true);
    try {
      const blob = await fetchExport(userId, {
        from: month.startOf("month").format(DATE),
        to: month.endOf("month").format(DATE),
        format,
        lng: (i18n.language || "de").slice(0, 2)
      });
      const base = (i18n.language || "de").startsWith("de") ? "Arbeitszeitnachweis" : "Timesheet";
      await saveFile(blob, `${base}_${month.format("YYYY-MM")}.${format}`);
    } catch (error) {
      const text = error.response?.data?.message;
      message.error(text ? t(text) : t("Export failed"));
    } finally {
      setExporting(false);
    }
  };
  const openEditor = date => setEditor({ open: true, date: date || dayjs().format(DATE) });
  const projectName = id => {
    const key = id?._id || id;
    return id?.projectName || projects.find(p => p._id === key)?.projectName;
  };

  // ── Month table ──────────────────────────────────────────────
  const rows = monthDays.map(d => {
    const open = (d.segments || []).some(s => !s.end);
    // A running day shows its time so far, like the clock card
    const running = open
      ? summarizeDay(d.segments, {
          now: Date.now(),
          flatBreakMinutes: d.flatBreakMinutes,
          targetMinutes: hoursToMinutes(d.targetHours)
        })
      : null;
    return {
      ...d,
      key: d._id,
      day: dayjs(d.date).format(DATE),
      paid: running ? running.paidMinutes : hoursToMinutes(d.totalWorkingHours),
      travel: running ? running.travelPaidMinutes : hoursToMinutes(d.totalTravelHours),
      breaks: running
        ? running.breakMinutes + running.flatBreakMinutes
        : Math.round(d.totalBreakTime || 0),
      // An unfinished day has no balance yet
      balance: running
        ? null
        : d.targetHours
          ? hoursToMinutes(d.totalWorkingHours - d.targetHours)
          : hoursToMinutes(d.overTime),
      edited: (d.segments || []).some(s => s.source === "MANUAL"),
      open
    };
  });
  const totals = rows.reduce(
    (sum, r) => ({
      paid: sum.paid + r.paid,
      travel: sum.travel + r.travel,
      balance: sum.balance + (r.balance || 0)
    }),
    { paid: 0, travel: 0, balance: 0 }
  );

  const columns = [
    {
      title: t("Date"),
      dataIndex: "day",
      key: "day",
      mobile: "title",
      render: day => (
        <>
          {dayjs(day).format("dd, DD.MM.")}
          {monthHolidays[day] && (
            <Tooltip title={monthHolidays[day]}>
              <Tag color="purple" className="attendance-page__holiday">
                {t("Holiday")}
              </Tag>
            </Tooltip>
          )}
        </>
      )
    },
    {
      title: t("From – to"),
      key: "span",
      mobile: "subtitle",
      render: (_, r) => {
        const sorted = (r.segments || [])
          .slice()
          .sort((a, b) => new Date(a.start) - new Date(b.start));
        if (!sorted.length) return "–";
        const last = sorted[sorted.length - 1];
        return `${dayjs(sorted[0].start).format("HH:mm")} – ${
          last.end ? dayjs(last.end).format("HH:mm") : "…"
        }`;
      }
    },
    {
      title: t("Working time"),
      dataIndex: "paid",
      key: "paid",
      align: "right",
      render: v => formatMinutes(v)
    },
    {
      title: t("of which travel"),
      dataIndex: "travel",
      key: "travel",
      align: "right",
      render: v => (v ? formatMinutes(v) : "–")
    },
    {
      title: t("Breaks"),
      dataIndex: "breaks",
      key: "breaks",
      align: "right",
      render: v => (v ? formatMinutes(v) : "–")
    },
    {
      title: t("Balance"),
      dataIndex: "balance",
      key: "balance",
      align: "right",
      render: v =>
        v === null ? (
          "–"
        ) : (
          <Text type={v < 0 ? "danger" : v > 0 ? "success" : undefined}>
            {v > 0 ? "+" : ""}
            {formatMinutes(v)}
          </Text>
        )
    },
    {
      title: t("Status"),
      key: "status",
      mobile: "badge",
      render: (_, r) => (
        <span className="attendance-page__tags">
          {r.open && <Tag color="blue">{t("Running")}</Tag>}
          {(r.warnings || []).length > 0 && (
            <Tooltip title={(r.warnings || []).map(w => warningText(t, w)).join(" · ")}>
              <Tag color="warning">{t("Check")}</Tag>
            </Tooltip>
          )}
          {r.edited && <Tag>{t("Edited")}</Tag>}
        </span>
      )
    },
    {
      title: "",
      key: "actions",
      mobile: "actions",
      render: (_, r) =>
        isLocked(r.day) ? (
          <Button icon={<EyeOutlined />} onClick={() => openEditor(r.day)}>
            {t("View")}
          </Button>
        ) : (
          <Button icon={<EditOutlined />} onClick={() => openEditor(r.day)}>
            {t("Edit")}
          </Button>
        )
    }
  ];

  const statRows = [
    {
      key: "today",
      label: t("Today"),
      worked: statistics?.today?.worked,
      target: statistics?.today?.target,
      color: token.colorPrimary
    },
    {
      key: "week",
      label: t("This Week"),
      worked: statistics?.thisWeek?.worked,
      target: statistics?.thisWeek?.target,
      color: token.colorWarning
    },
    {
      key: "month",
      label: t("This Month"),
      worked: statistics?.thisMonth?.worked,
      target: statistics?.thisMonth?.target,
      color: token.colorSuccess
    }
  ];

  if (!today) {
    return (
      <div className="attendance-page attendance-page__loading">
        <Spin size="large" />
      </div>
    );
  }

  const statusMeta = STATUS_META[status] || STATUS_META.NOT_STARTED;
  const sortedToday = segments.slice().sort((a, b) => new Date(a.start) - new Date(b.start));

  return (
    <div className="attendance-page">
      {openDays.map(day => (
        <Alert
          key={day}
          type="warning"
          showIcon
          message={t("The end time of {{date}} is missing.", {
            date: dayjs(day).format("DD.MM.YYYY")
          })}
          description={isLocked(day) ? t("Please ask your manager to enter it.") : undefined}
          action={
            isLocked(day) ? undefined : (
              <Button size="small" onClick={() => openEditor(day)}>
                {t("Fix now")}
              </Button>
            )
          }
        />
      ))}

      <Row gutter={[16, 16]} className="attendance-page__cards">
        {/* Clock */}
        <Col xs={24} md={12} xl={8}>
          <Card className="attendance-main-cards">
            <h5 className="attendance-page__card-title">
              {t("Timesheet")}{" "}
              <Text type="secondary" className="attendance-page__date">
                {dayjs(today.date).format("dd, DD.MM.YYYY")}
              </Text>
            </h5>
            <OfflineNotice module="attendance" record={{ _id: userId }} />

            <div className="attendance-page__status">
              <div>
                <div className="attendance-page__label">{t("Status")}</div>
                <Tag color={statusMeta.color}>{t(statusMeta.label)}</Tag>
              </div>
              <div>
                <div className="attendance-page__label">
                  {running ? t("since") : t("Current Time")}
                </div>
                <Text type="secondary">
                  {running
                    ? `${dayjs(running.start).format("HH:mm")} · ${formatMinutes(
                        (Date.now() - new Date(running.start)) / 60000
                      )} ${t("h")}`
                    : dayjs().format("HH:mm")}
                </Text>
              </div>
            </div>

            <div className="attendance-page__hours">
              <Statistic
                title={t("Working Hours Today")}
                value={formatMinutes(live.paidMinutes)}
                suffix={t("h")}
                valueStyle={{ color: token.colorSuccess, fontSize: 28, fontWeight: 700 }}
              />
              {live.travelPaidMinutes > 0 && (
                <Text type="secondary">
                  {t("of which travel")} {formatMinutes(live.travelPaidMinutes)} {t("h")}
                </Text>
              )}
            </div>

            <div className="attendance-page__when">
              <Segmented
                value={timeMode}
                onChange={mode => {
                  // Start from the current time, not the one the page was opened at
                  if (mode === "custom") setCustomTime(dayjs().format("HH:mm"));
                  setTimeMode(mode);
                }}
                options={[
                  { value: "now", label: t("Now") },
                  { value: "custom", label: t("Other time") }
                ]}
              />
              {timeMode === "custom" && (
                <Input
                  type="time"
                  value={customTime}
                  onChange={e => setCustomTime(e.target.value)}
                  aria-label={t("Time")}
                  className="attendance-page__time"
                />
              )}
            </div>

            {(ACTIONS[status] || []).some(a => a === "WORK" || a === "TRAVEL") &&
              projects.length > 0 && (
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("Project (optional)")}
                  value={clockProject || undefined}
                  onChange={v => setClockProject(v || null)}
                  options={projects.map(p => ({ value: p._id, label: p.projectName }))}
                  className="attendance-page__project"
                />
              )}

            <div className="attendance-page__actions">
              {(ACTIONS[status] || []).map(actionButton)}
            </div>

            <Button type="link" icon={<EditOutlined />} onClick={() => openEditor(today.day)} block>
              {t("Enter or correct times manually")}
            </Button>
          </Card>
        </Col>

        {/* Statistics */}
        <Col xs={24} md={12} xl={8}>
          <Card className="attendance-main-cards">
            <h5 className="attendance-page__card-title">{t("Statistics")}</h5>
            {statRows.map(row => (
              <div className="stats-info" key={row.key}>
                <p>
                  {row.label}{" "}
                  <strong>
                    {(row.worked || 0).toFixed(1)}
                    <small>
                      {" "}
                      / {(row.target || 0).toFixed(1)} {t("hrs")}
                    </small>
                  </strong>
                </p>
                <Progress
                  percent={
                    row.target
                      ? Math.min(((row.worked || 0) / row.target) * 100, 100).toFixed(1)
                      : 0
                  }
                  strokeColor={row.color}
                  size="small"
                />
              </div>
            ))}
            <div className="stats-info">
              <p>
                {t("Total Overtime")}{" "}
                <strong>
                  {(statistics?.overtime || 0).toFixed(1)}
                  <small> {t("hrs")}</small>
                </strong>
              </p>
            </div>
          </Card>
        </Col>

        {/* Today's blocks */}
        <Col xs={24} md={24} xl={8}>
          <Card className="attendance-main-cards">
            <h5 className="attendance-page__card-title">{t("Today's Activity")}</h5>
            {sortedToday.length ? (
              <div className="attendance-page__timeline">
                <Timeline
                  items={sortedToday.map(s => {
                    const minutes =
                      ((s.end ? new Date(s.end) : Date.now()) - new Date(s.start)) / 60000;
                    return {
                      color:
                        TYPE_META[s.type]?.color === "default" ? "gray" : TYPE_META[s.type]?.color,
                      children: (
                        <div>
                          <strong>{t(TYPE_META[s.type]?.label || s.type)}</strong>{" "}
                          <Text type="secondary" className="attendance-page__time">
                            {dayjs(s.start).format("HH:mm")} –{" "}
                            {s.end ? dayjs(s.end).format("HH:mm") : t("running")} ·{" "}
                            {formatMinutes(minutes)} {t("h")}
                          </Text>
                          {s.type === "TRAVEL" && s.paid === false && (
                            <Tag className="attendance-page__inline-tag">{t("unpaid")}</Tag>
                          )}
                          {projectName(s.project) && (
                            <div>
                              <Text type="secondary">{projectName(s.project)}</Text>
                            </div>
                          )}
                          {s.note && (
                            <div>
                              <Text type="secondary" italic>
                                {s.note}
                              </Text>
                            </div>
                          )}
                        </div>
                      )
                    };
                  })}
                />
              </div>
            ) : (
              <Text type="secondary">{t("No times recorded yet today.")}</Text>
            )}
            {live.warnings.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={live.warnings.map(w => warningText(t, w)).join(" · ")}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Month */}
      <Card className="attendance-page__history">
        <div className="attendance-page__month-head">
          <h5 className="attendance-page__card-title">{t("Working time record")}</h5>
          <div className="attendance-page__month-tools">
            <DatePicker
              picker="month"
              value={month}
              allowClear={false}
              format="MMMM YYYY"
              className="attendance-page__month-picker"
              onChange={m => m && setMonth(m.startOf("month"))}
              disabledDate={d => d.isAfter(dayjs(), "month")}
            />
            <Dropdown
              trigger={["click"]}
              menu={{
                onClick: ({ key }) => exportMonth(key),
                items: [
                  { key: "pdf", icon: <FilePdfOutlined />, label: t("PDF (for signing)") },
                  { key: "xlsx", icon: <FileExcelOutlined />, label: t("Excel (.xlsx)") },
                  { key: "csv", icon: <FileTextOutlined />, label: t("CSV") },
                  ...(canExportTeam && !employeeId
                    ? [
                        { type: "divider" },
                        {
                          type: "group",
                          key: "team",
                          label: t("Team (choose employees)"),
                          children: [
                            { key: "team:pdf", icon: <FilePdfOutlined />, label: t("Team PDF") },
                            { key: "team:xlsx", icon: <FileExcelOutlined />, label: t("Team Excel") },
                            { key: "team:csv", icon: <FileTextOutlined />, label: t("Team CSV") }
                          ]
                        }
                      ]
                    : [])
                ]
              }}
            >
              <Button icon={<DownloadOutlined />} loading={exporting}>
                {t("Export")}
              </Button>
            </Dropdown>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor()}>
              {t("Enter a day")}
            </Button>
          </div>
        </div>
        <div className="attendance-page__month-totals">
          <span>
            <Text type="secondary">{t("Working time")}</Text>{" "}
            <strong>
              {formatMinutes(totals.paid)} {t("h")}
            </strong>
          </span>
          <span>
            <Text type="secondary">{t("of which travel")}</Text>{" "}
            <strong>
              {formatMinutes(totals.travel)} {t("h")}
            </strong>
          </span>
          <span>
            <Text type="secondary">{t("Balance")}</Text>{" "}
            <Text
              strong
              type={totals.balance < 0 ? "danger" : totals.balance > 0 ? "success" : undefined}
            >
              {totals.balance > 0 ? "+" : ""}
              {formatMinutes(totals.balance)} {t("h")}
            </Text>
          </span>
        </div>
        <ResponsiveTable
          dataSource={rows}
          columns={columns}
          rowKey="key"
          loading={monthLoading}
          mobileAvatar={false}
          pagination={false}
          onRow={record => ({
            onClick: e => !e.target.closest("button") && openEditor(record.day)
          })}
          locale={{ emptyText: t("No times recorded in this month.") }}
        />
      </Card>

      <TeamExportModal
        open={teamExport.open}
        month={month}
        initialFormat={teamExport.format}
        onClose={() => setTeamExport(e => ({ ...e, open: false }))}
      />

      <DayEditor
        open={editor.open}
        date={editor.date}
        userId={userId}
        projects={projects}
        onClose={() => setEditor(e => ({ ...e, open: false }))}
        onSaved={refresh}
      />
    </div>
  );
};

export default AttendanceComp;
